<template>
  <modal
    :show="show"
    @close="$emit('close')"
    class="add-practice-modal prompt-modal text-left"
    :containerStyle="{ maxWidth: '500px', maxHeight: 'min(600px, 90vh)', padding: '3.375rem 0rem' }"
  >
    <a @click.prevent="$emit('close')" class="close" title="Close" href="javascript:void(0)">
      <font-awesome-icon :icon="['far', 'times']" />
    </a>
    <div class="modal-header">
      <div class="">
        <div class="alert-danger alert" v-if="isPublicLoggedIn">
          <b>Account required</b><br />
          Please <router-link to="/login">login</router-link> or <router-link to="/register">signup</router-link> in
          order to curate your own network.
        </div>
        <h3 class="m-0">Add new practice</h3>
      </div>
      <div class="col text-right"></div>
    </div>
    <template v-if="step == 0">
      <div class="modal-body d-flex">
        <p class="lead" style="max-width: 500px">Tell us the phone number of the practice you would like to add.</p>
        <div class="d-flex flex-column">
          <div :class="bodyClass">
            <div class="form-group text-left">
              <label>Practice phone</label>
              <input
                :value="practicePhone | phone"
                @input="phoneInput"
                @keyup.enter="lookup"
                type="text"
                class="form-control form-control-lg form-control-xl d-block"
                placeholder="(123) 456-7890"
                v-validate="'required'"
                name="practicePhone"
                data-vv-as="practice phone"
                :disabled="isSearching || isPublicLoggedIn"
              />
              <span v-if="errors.has('practicePhone')" class="invalid-feedback d-block">{{
                errors.first('practicePhone')
              }}</span>
              <!-- <small><a href="javascript:void(0);">I don't know the phone number</a></small> -->
            </div>
          </div>
        </div>
      </div>
      <template v-if="step == 'loading'">
        <div class="modal-body d-flex">
          <div class="d-flex flex-column align-items-center justify-content-center">
            <div :class="bodyClass">
              <p class="lead" style="max-width: 500px">We're just checking our rolladex...</p>
            </div>
          </div>
        </div>
      </template>
      <div class="modal-footer">
        <div class="d-flex justify-content-end" :class="buttonContainerClass">
          <!-- <button @click="step = 0" class="btn btn-link btn-xl" :disabled="isSearching">
            Back
          </button> -->
          <button @click.prevent="lookup" class="btn btn-primary btn-xl" :disabled="isSearching || isPublicLoggedIn">
            <font-awesome-icon icon="spinner-third" v-if="isSearching" spin /><span v-else>Continue</span>
          </button>
        </div>
      </div>
    </template>
    <template v-if="step == 1">
      <template v-if="results.length == 1">
        <div class="modal-body d-flex align-items-center">
          <div class="d-flex flex-column w-100">
            <p class="lead" style="max-width: 500px">
              We found a practice. Is this <b class="font-weight-bold">correct</b>?
            </p>
            <b>{{ results[0].name }}</b>
            <address>
              {{ $helpers.join([results[0].address, results[0].address_2]) }}
              <span class="d-block">{{ $helpers.cityStateZip(results[0]) }}</span>
            </address>
          </div>
        </div>
        <div class="modal-footer">
          <div class="d-flex justify-content-between" :class="buttonContainerClass">
            <button @click.prevent="correctAddress" class="btn btn-link btn-xl" :disabled="isAdding">
              No, {{ results[0].status == 0 ? 'let me correct it' : 'go back' }}
            </button>
            <button @click.prevent="select" class="btn btn-primary btn-xl" :disabled="isAdding">
              <font-awesome-icon icon="spinner-third" v-if="isAdding" spin /><span v-else>Yes</span>
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="modal-body">
          <!-- <div class="d-flex">
            <div v-for="result in results" :key="result.id" class="border rounded px-3 py-2 mb-2 d-flex flex-column">
              <b>{{result.name}}</b>
              <address v-html="$helpers.address(result, '<br />')"></address>
            </div>
          </div> -->
          <h4 v-if="results.length === 2">We found two potential matches, <b>which location</b>?</h4>
          <h4 v-else>
            We found <u>{{ results.length }}</u> potential matches, <b>which location</b>?
          </h4>
          <div
            v-for="result in results"
            :key="result.id"
            class="address-result border rounded px-3 py-2 mb-2"
            :class="{ selected: existing == result.id }"
          >
            <div class="form-check d-flex align-items-center">
              <input
                :id="`addressSelect${result.id}`"
                class="form-check-input"
                type="radio"
                v-model="existing"
                :value="result.id"
              />
              <label :for="`addressSelect${result.id}`" class="form-check-label ml-3">
                {{ $helpers.join([result.address, result.address_2]) }}
                <small class="text-muted d-flex ml-auto align-items-center text-right">
                  {{ $helpers.join([result.city, result.state]) }}</small
                ></label
              >
            </div>
          </div>
          <div class="border rounded px-3 py-2 address-result" :class="{ selected: existing == -1 }">
            <div class="form-check">
              <!-- todo: allow multiple location -->
              <input type="radio" v-model="existing" class="form-check-input" id="addressSelectNone" :value="-1" />
              <label for="addressSelectNone" class="form-check-label ml-3"> None of the above </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="d-flex justify-content-between" :class="buttonContainerClass">
            <button @click.prevent="step = 0" class="btn btn-link btn-xl" :disabled="isAdding">Back</button>
            <button @click.prevent="select" class="btn btn-primary btn-xl" :disabled="isAdding || !existing">
              <font-awesome-icon icon="spinner-third" v-if="isAdding" spin /><span v-else>Next</span>
            </button>
          </div>
        </div>
      </template>
    </template>
    <template v-if="step == 2">
      <div class="modal-body d-flex align-items-center">
        <div class="d-flex flex-column w-100" style="min-width: 320px">
          <form @submit.prevent="add">
            <div class="form-group">
              <label>Practice name</label>
              <input
                v-model="practiceName"
                type="text"
                class="form-control form-control-lg form-control-xl d-block"
                placeholder="Practice Name"
                v-validate="'required'"
                name="practiceName"
                data-vv-as="practice name"
              />
              <span v-if="errors.has('practiceName')" class="invalid-feedback d-block">{{
                errors.first('practiceName')
              }}</span>
            </div>
            <div class="form-group">
              <label>Practice address</label>

              <div class="autocomplete-lg autocomplete-xl">
                <autocomplete
                  @selected="addressSelected"
                  required
                  v-validate="'required'"
                  name="address"
                  data-vv-as="practice address"
                  :value="
                    selectedAddress &&
                    [selectedAddress.address, selectedAddress.address_2, selectedAddress.city, selectedAddress.state]
                      .filter((s) => s)
                      .join(', ')
                  "
                />
              </div>
              <span v-if="errors.has('address')" class="invalid-feedback d-block">{{ errors.first('address') }}</span>
            </div>
            <div class="form-group">
              <label>Practice speciality</label>
              <select
                class="form-control form-control-lg form-control-xl"
                required
                v-model="specialty"
                v-validate="'required'"
                name="specialty"
                data-vv-as="practice specialty"
              >
                <option disabled selected :value="null">Select Specialty</option>
                <option v-for="(s, i) in PracticeTypes" :value="i" :key="s">{{ s }}</option>
              </select>
              <span v-if="errors.has('specialty')" class="invalid-feedback d-block">{{
                errors.first('specialty')
              }}</span>
            </div>
            <div class="form-group">
              <label>Practice email</label>
              <input
                v-model="email"
                type="email"
                class="form-control form-control-lg form-control-xl"
                placeholder="Email e.g info@office.com"
                v-validate="'required'"
                name="practiceEmail"
                data-vv-as="practice email"
              />
              <span v-if="errors.has('email')" class="invalid-feedback d-block">{{ errors.first('email') }}</span>
            </div>
          </form>
        </div>
      </div>
      <div class="modal-footer">
        <div class="text-right" :class="buttonContainerClass">
          <button @click.prevent="step = results.length ? 1 : 0" class="btn btn-link btn-xl" :disabled="isAdding">
            Back
          </button>
          <button @click.prevent="add" class="btn btn-primary btn-xl" :disabled="isAdding">
            <font-awesome-icon icon="spinner-third" v-if="isAdding" spin /><span v-else>Next</span>
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import * as Sentry from '@sentry/vue'
import NpiService from '@/services/NpiService'
import Autocomplete from '@/components/shared/address-lookup/Autocomplete'
import { mapState, mapGetters } from 'vuex'
import Modal from '@/components/Modal'

export default {
  components: { Autocomplete, Modal },
  props: ['buttonContainerClass', 'bodyClass', 'show'],
  data() {
    return {
      isSearching: false,
      isAdding: false,
      practiceName: null,
      practicePhone: null,
      practiceNpi: 'MANUAL_ADD',
      specialty: null,
      practiceId: null,
      results: [],
      existing: null,
      step: 0,
      selectedAddress: {},
      email: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isPublicLoggedIn']),
    ...mapGetters('settings', ['PracticeTypes']),
  },
  $_veeValidate: {
    validator: 'new', // give me my own validator scope.
  },
  methods: {
    phoneInput(e) {
      this.practicePhone = e.target?.value?.replace(/[^\d]/g, '').substring(0, 10)
    },
    reset() {
      this.practiceName = null
      this.specialty = null
      this.practiceId = null
      this.practiceNpi = 'MANUAL_ADD'
      this.selectedAddress = null
    },
    async lookup(e) {
      e.preventDefault()
      this.reset()
      const valid = await this.$validator.validate()
      if (!valid) return
      this.isSearching = true
      try {
        this.results = await NpiService.byPhone(this.practicePhone)
        this.step = this.results.length ? 1 : 2
      } catch (e) {}
      this.isSearching = false
    },
    async select() {
      if (this.existing == null) {
        if (!this.results?.length) return
        this.existing = this.results?.[0]?.id
        if (this.existing == null) return
      }
      //todo: emit new id
      if (this.existing == -1) {
        this.step = 2 //show address editor
        return
      }
      await this.addToNetwork({ id: this.existing })
      this.$emit(
        'selected',
        this.results.find((s) => s.id == this.existing)
      )
    },
    async addressSelected(address) {
      this.selectedAddress = address
    },
    async add(address) {
      const valid = await this.$validator.validate()
      if (!valid) return
      this.isAdding = true
      //todo: should check location does not already exist - since they could match a result
      try {
        this.practice = await this.$store.dispatch('practices/create', {
          name: this.practiceName,
          phone: this.practicePhone,
          email: this.email,
          ...this.selectedAddress,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // todo: do on backend based on location, or update it when the receiver accepts
          specialty: this.specialty,
          npi: this.practiceNpi,
          id: this.practiceId,
        })
        if (this.user?.practice) {
          await this.addToNetwork(this.practice)
        }
        this.$emit('selected', this.practice)
      } catch (e) {
        this.$helpers.error('An error occured')
        console.error(e)
        Sentry.captureException(e)
      }
      this.isAdding = false
    },
    async addToNetwork(p) {
      if (!this.user?.practice) return
      try {
        await this.$store.dispatch('practices/addToNetwork', p)
      } catch (e) {
        this.$helpers.error('Failed to add practice')
        console.error(e)
        Sentry.captureException(e)
      }
    },
    correctAddress(e) {
      const result = this.results?.[0]
      // const isSingle = this.results.length == 1 //should always be true since this is only called from
      if (!result || result.status > 0) {
        this.step = 0
        return
      }
      if (result?.status == 0) {
        this.practiceName = result.name
        this.practiceId = result.id
        this.practiceNpi = result.npi
        this.selectedAddress = {
          address: result.address,
          address_2: result.address_2,
          city: result.city,
          state: result.state,
          zip: result.zip,
        }
        this.specialty = result.specialty
        this.step = 2
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.address-result {
  cursor: pointer;

  .form-check {
    display: flex;
    padding-left: 0;

    .form-check-input {
      position: inherit;
      /* position: absolute; */
      margin: 0;
    }

    label {
      cursor: pointer;
      flex: 1 1 auto;
      font-size: 0.85rem;
    }
  }

  &:hover,
  &.selected {
    border: 1px solid var(--border-color) !important;
  }

  border: 1px solid var(--border-color);
}

.add-practice-modal {
  label:not(.form-check-label) {
    font-size: 0.85rem;
    color: #8c9797;
  }

  .modal-body {
    h4 {
      font-weight: normal;
    }

    height: 100%;
    // padding: 1rem 2rem !important;
    overflow: auto;
    display: block;
  }

  .modal-footer {
    width: 100%;
  }
}

h4 {
  margin-bottom: 1rem;
}

#app .add-practice-modal.prompt-modal .modal-container {
  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 4.6875rem;
    padding-right: 4.6875rem;
  }

  .modal-body {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>